import React, { useEffect, useState } from 'react';

const PrintOrderPage = () => {
    const [data, setData] = useState(null);

    const printTriggered = React.useRef(false);

    useEffect(() => {
        try {
            const storedData = localStorage.getItem('printInvoiceData');
            if (storedData) {
                setData(JSON.parse(storedData));
                // Wait for render then print, ensuring only once
                if (!printTriggered.current) {
                    printTriggered.current = true;
                    setTimeout(() => {
                        window.print();
                    }, 500);
                }
            }
        } catch (err) {
            console.error("Failed to load print data", err);
        }
    }, []);

    if (!data || !data.order) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <p className="text-gray-500">No invoice data found to print.</p>
            </div>
        );
    }

    const { order, supplier: supplierData, customer: fallbackCustomer } = data;
    const customer = order.customer || fallbackCustomer || {};

    // Helper to get formatted date
    // Helper to get formatted date
    const formatDate = (dateString) => {
        if (!dateString) return 'Pending';
        return new Date(dateString).toLocaleDateString('en-GB');
    };

    return (
        <div className="min-h-screen bg-slate-100 py-6 print:py-0 print:bg-white flex justify-center items-start">
            {/* Print Invoice Layout (Visible only on print, formatted for 4" thermal receipt) */}
            <div className="print-invoice-wrapper w-[100mm] bg-white p-4 print:p-4 shadow-md print:shadow-none font-sans text-gray-900">
                {/* Header */}
                <div className="text-center pb-3 border-b border-dashed border-gray-400 mb-3 space-y-1">
                    <div className="flex items-center justify-center gap-2">
                        {supplierData?.logoUrl ? (
                            <img src={supplierData.logoUrl} alt="Logo" className="w-8 h-8 object-contain rounded-lg border border-gray-100" />
                        ) : (
                            <div className="w-8 h-8 bg-blue-600 text-white font-black text-sm rounded-lg flex items-center justify-center">
                                {(supplierData?.businessName || 'S').charAt(0)}
                            </div>
                        )}
                        <h2 className="text-base font-black tracking-tight text-gray-900 uppercase">
                            {supplierData?.businessName || 'Supplier'}
                        </h2>
                    </div>
                    <h1 className="text-sm font-black tracking-tight text-gray-800 uppercase">INVOICE</h1>
                    <p className="text-xs font-black text-gray-800">#{order.orderRefNo || order.publicId.substring(0, 8)}</p>
                    <div className="mt-1">
                        <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-800 text-[9px] font-bold border uppercase">
                            {order.status || 'New'}
                        </span>
                    </div>
                </div>

                {/* Info Block (Vertical stack to prevent overlaps on narrow thermal printouts) */}
                <div className="space-y-3 text-xs mb-3">
                    {/* Supplier */}
                    <div className="pb-2 border-b border-dashed border-gray-200">
                        <h3 className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Supplier</h3>
                        <p className="font-bold text-gray-900">{supplierData?.businessName || 'Supplier'}</p>
                        <p className="text-gray-600 text-[11px] leading-tight mt-0.5">{supplierData?.address || 'Address N/A'}</p>
                        <p className="text-gray-600 text-[11px]">📞 {supplierData?.phone || 'N/A'}</p>
                    </div>

                    {/* Customer */}
                    <div className="pb-2 border-b border-dashed border-gray-200">
                        <h3 className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Bill To</h3>
                        <p className="font-bold text-gray-900">{customer.name || customer.customerName || 'Guest'}</p>
                        <p className="text-gray-600 text-[11px] leading-tight mt-0.5">{customer.address || customer.location || customer.city || 'No Address'}</p>
                        <p className="text-gray-600 text-[11px]">📱 {customer.mobileNumber || customer.whatsappNumber || customer.mobile || 'N/A'}</p>
                    </div>

                    {/* Order Metadata (2-column layout fits perfectly on 4" format) */}
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 pb-2 border-b border-dashed border-gray-200 text-[11px]">
                        <div>
                            <span className="block text-[9px] font-bold text-gray-400 uppercase">Order Date</span>
                            <span className="font-semibold text-gray-900">
                                {order.createdAt ? new Date(order.createdAt).toLocaleDateString('en-GB') : new Date().toLocaleDateString('en-GB')}
                            </span>
                        </div>
                        <div>
                            <span className="block text-[9px] font-bold text-gray-400 uppercase">Delivery Date</span>
                            <span className="font-semibold text-gray-900">
                                {order.deliveryDate ? new Date(order.deliveryDate).toLocaleDateString('en-GB') : 'Pending'}
                            </span>
                        </div>
                        <div>
                            <span className="block text-[9px] font-bold text-gray-400 uppercase">Sales Person</span>
                            <span className="font-semibold text-gray-900 truncate block max-w-[120px]">{order.assignedSalesPersonName || 'Unassigned'}</span>
                        </div>
                        <div>
                            <span className="block text-[9px] font-bold text-gray-400 uppercase">Time Slot</span>
                            <span className="font-semibold text-gray-900">{order.timeSlot || 'Any time'}</span>
                        </div>
                    </div>
                </div>

                {/* Items Section */}
                <div className="mb-3">
                    <h3 className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Items</h3>
                    <div className="space-y-2.5">
                        {order.items?.map((item, idx) => (
                            <div key={idx} className="text-xs pb-1.5 border-b border-dashed border-gray-100 last:border-b-0">
                                <div className="font-bold text-gray-900 leading-tight">{item.productName || item.name}</div>
                                <div className="flex justify-between text-[11px] text-gray-600 mt-0.5">
                                    <span>{item.quantity} {item.unit || 'pcs'} x AED {(item.unitPrice || item.price || 0).toFixed(2)}</span>
                                    <span className="font-bold text-gray-900">AED {(item.subtotal || item.totalPrice || (item.quantity * (item.unitPrice || item.price || 0))).toFixed(2)}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Footer Totals */}
                <div className="border-t border-double border-gray-900 pt-2 flex justify-between items-center text-xs">
                    <span className="font-bold uppercase text-gray-700">Total Amount</span>
                    <span className="text-base font-black text-gray-950">AED {(order.totalAmount || 0).toFixed(2)}</span>
                </div>

                {/* Thank you */}
                <div className="text-center pt-5 text-[9px] text-gray-500 font-semibold uppercase tracking-wider border-t border-dashed border-gray-300 mt-4 font-sans">
                    Thank You for Your Order!
                </div>
            </div>

            <style>{`
                @media print {
                    @page {
                        size: 100mm auto;
                        margin: 0;
                    }
                    body {
                        background-color: #ffffff;
                        margin: 0;
                        padding: 0;
                    }
                    .print-invoice-wrapper {
                        width: 100mm !important;
                        padding: 4mm !important;
                        box-sizing: border-box !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default PrintOrderPage;
